import { Grid, InputAdornment, TextField } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { DateRangePicker } from 'app/ComponentLibrary/DateRangeNew';
import { NumberField } from 'app/components/Candidate/CandidateProfile/CandidateTabPanel/PreferencesTab/CustomComponents/Currency';
import _ from 'lodash';
import { Moment } from 'moment';
import React, { useContext, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { GetMomentOfDate } from 'utils/dates/moment';
import { ExtensionContext } from './ExtensionForm';
import { getExtensionEndDate } from './helper';
import { ExtEditTarget } from '../PlacementDetails/PlacementTabPanel/PlacementSummaryTab/Statuses/Extensions/ExtensionEditIcon';
import { useDispatch, useSelector } from 'react-redux';
import { pageAttributesActions } from '../PlacementDetails/Edit/store/placementPageAttributes.redux';
import { selectPlacementStatus } from 'store/redux-store/placement-status/selectors';
import { selectExtensionModalProps } from '../PlacementDetails/Edit/store/placementPageAttributes.selector';
import { getExtensionDuration } from 'app/helpers/getExtensionDuration';

const useStyles = makeStyles()(theme => ({
  duration: {
    width: 160,
  },
  datePicker: {
    width: 190,
  },
}));

export const ExtendDuration = () => {
  const { classes } = useStyles();
  const { startDateMoment, disabledFields, currentTargetField, isNewExtension } = useContext(ExtensionContext);
  const {
    extensionDetails: { endDateTravelerOption },
  } = useSelector(selectPlacementStatus);
  const { extensionStatusId } = useSelector(selectExtensionModalProps);

  const {
    formState: {
      dirtyFields: { endDate: isEndDateDirty, duration: isDurationDirty },
    },
  } = useFormContext();

  const overrideEndDateModPrompt = useMemo(
    () => endDateTravelerOption?.approvalStatusId === 4 && !isNewExtension && !(isEndDateDirty || isDurationDirty),
    [isNewExtension, endDateTravelerOption?.approvalStatusId, isEndDateDirty, isDurationDirty],
  );

  const dispatch = useDispatch();

  const openOverridePrompt = callbackFn => {
    dispatch(
      pageAttributesActions.setPreExtensionModalProps({
        open: true,
        title: `How would you like to proceed?`,
        extensionStatusId,
        callbackFn,
      }),
    );
  };

  return (
    <Grid container spacing={2} wrap="nowrap">
      <Grid item>
        <Duration
          startDateMoment={startDateMoment}
          disabled={disabledFields.duration}
          currentTargetField={currentTargetField}
          overrideEndDateModPrompt={overrideEndDateModPrompt}
          openOverridePrompt={openOverridePrompt}
          classes={classes}
        />
      </Grid>
      <Grid item>
        <StartDate disabled={disabledFields.startDate} classes={classes} />
      </Grid>
      <Grid item>
        <EndDate
          startDateMoment={startDateMoment}
          disabled={disabledFields.endDate}
          overrideEndDateModPrompt={overrideEndDateModPrompt}
          openOverridePrompt={openOverridePrompt}
          classes={classes}
        />
      </Grid>
    </Grid>
  );
};

export const Duration: React.FC<{
  startDateMoment: Moment;
  disabled: boolean;
  currentTargetField: ExtEditTarget;
  overrideEndDateModPrompt: boolean;
  openOverridePrompt: Function;
  classes;
}> = ({ startDateMoment, disabled, currentTargetField, overrideEndDateModPrompt, openOverridePrompt, classes }) => {
  const { control, formState: { errors }, setValue } = useFormContext();

  const changeValues = (newDuration, newEndDate, onChange) => {
    setValue('endDate', newEndDate.toDate(), { shouldDirty: true });
    onChange(newDuration);
  };

  const handleChange = (newState, onChange) => {
    if (!newState || (newState >= 0 && newState <= 999)) {
      const newEndDate = getExtensionEndDate(startDateMoment, newState ?? 0);
      overrideEndDateModPrompt
        ? openOverridePrompt(() => changeValues(newState, newEndDate, onChange))
        : changeValues(newState, newEndDate, onChange);
    }
  };

  return (
    <Controller
      name="duration"
      control={control}
      render={({ field: { onChange, ...rest } }) => (
        <CustomTextField
          autoFocus={!disabled && (!currentTargetField || currentTargetField === ExtEditTarget.date)}
          {...rest}
          onChange={newState => {
            handleChange(newState, onChange);
          }}
          error={!!errors.duration}
          helperText={!!errors.duration ? errors.duration.message : ' '}
          disabled={disabled}
          classes={{ root: classes.duration }}
        />
      )}
    />
  );
};

export const StartDate = ({ disabled, classes }) => {
  const { control } = useFormContext();
  return (
    <Controller
      name="startDate"
      control={control}
      render={({ field: { value, ...rest } }) => (
        <TextField
          value={GetMomentOfDate(value).format('MM/DD/YYYY')}
          variant="filled"
          label="Start Date"
          disabled={disabled}
          classes={{ root: classes.datePicker }}
          {...rest}
        />
      )}
    />
  );
};

export const EndDate: React.FC<{
  startDateMoment: Moment;
  overrideEndDateModPrompt: boolean;
  openOverridePrompt: Function;
  disabled: boolean;
  classes;
}> = ({ startDateMoment, overrideEndDateModPrompt, openOverridePrompt, disabled, classes }) => {
  const { control, setValue, formState: { errors } } = useFormContext();
  const [maxDate] = useState(_.cloneDeep(startDateMoment).add(999, 'w').toDate());

  const changeValues = (newDuration, newEndDate, onChange) => {
    setValue('duration', newDuration, { shouldDirty: false });
    onChange(newEndDate);
  };

  const handleChange = (newValue, onChange) => {
    const newDuration = getExtensionDuration(startDateMoment, newValue);

    overrideEndDateModPrompt
      ? openOverridePrompt(() => changeValues(newDuration, newValue, onChange))
      : changeValues(newDuration, newValue, onChange);
  };

  return (
    <Controller
      name="endDate"
      control={control}
      render={({ field: { onChange, value, ...rest } }) => (
        <DateRangePicker
          textFieldSize="medium"
          variant="filled"
          quickSelect={false}
          disablePortal={false}
          single
          placeholder="End Date *"
          trailingIconShow
          actionBar={false}
          useMaxWidth
          maxWidth={190}
          className={classes.datePicker}
          initialDateRange={{ startDate: value, endDate: value, useLabelAsValue: true }}
          clickAway
          onChange={({ startDate: newValue }) => handleChange(newValue, onChange)}
          maxDate={maxDate}
          isDisabled={disabled}
          {...rest}
        />
      )}
    />
  );
};

const CustomTextField = ({ value, onChange, ...rest }) => (
  <NumberField
    {...rest}
    onChange={e => onChange(parseInt(e.target.value))}
    type="number"
    label="Duration *"
    variant="filled"
    value={value}
    InputProps={{
      endAdornment: <InputAdornment position="start">{'Weeks'}</InputAdornment>,
    }}
  />
);
